import {
  As,
  ButtonGroup as ChakraButtonGroup,
  ButtonGroupProps,
  forwardRef,
  IconButtonProps,
} from '@chakra-ui/react';
import { IconButton } from '@main/complykit/components/icon-button';
import { Tooltip } from '@main/complykit/components/tooltip';
import { isNonNullable } from '@main/shared/utils';
import { ComponentType, SVGProps } from 'react';

type LinkButtonProps = {
  href: string;
  isExternal?: boolean;
};
type ClickButtonProps = {
  /** Press-invoked: no event, and presses do not bubble (see actions/types.ts). */
  onClick: () => void;
};
export type ButtonProps = (LinkButtonProps | ClickButtonProps) & {
  icon: As;
  tooltip?: string;
  disabledTooltip?: string;
} & Omit<IconButtonProps, 'icon'>;

export type ActionButtonProps = {
  buttons: (ButtonProps | null)[];
  disableTooltips?: boolean;
} & ButtonGroupProps;

/* The interim ActionCluster (component-map.md): one surfaced pill floating
 * over content, ghost actions at zero gap inside a bordered raised surface.
 * The row clusters and the drawer hover actions share it until the cluster
 * earns its ComplyKit name. */
export const ActionClusterShell = forwardRef<ButtonGroupProps, 'div'>((props, ref) => (
  // The pill reads as ONE surface: ghost buttons at zero spacing inside a
  // bordered surface, replacing the old attached-outline group without
  // clipping the inner focus rings.
  <ChakraButtonGroup
    {...props}
    ref={ref}
    spacing={0}
    bg="var(--ck-surface-raised)"
    border="1px solid"
    borderColor="var(--ck-border-default)"
    borderRadius="var(--ck-radius-3)"
    boxShadow="sm"
    /* Cluster-only shaping of the ghost squares: hover washes go square so
     * the strip reads as one control, the two end buttons keep only the
     * pill's outer corners (radius minus the border, so the wash meets the
     * border), and a hairline divider separates neighbours. Scoped to the
     * controls themselves because a seat's non-portaled menu list is also a
     * child here. No overflow clipping, so the inner focus rings stay whole. */
    sx={{
      '& > :is(button, a, span)': { borderRadius: 0 },
      '& > :is(button, a, span):first-of-type': {
        borderTopLeftRadius: 'calc(var(--ck-radius-3) - 1px)',
        borderBottomLeftRadius: 'calc(var(--ck-radius-3) - 1px)',
      },
      '& > :is(button, a, span):last-of-type': {
        borderTopRightRadius: 'calc(var(--ck-radius-3) - 1px)',
        borderBottomRightRadius: 'calc(var(--ck-radius-3) - 1px)',
      },
      '& > :is(button, a, span) + :is(button, a, span)': {
        borderLeft: '1px solid var(--ck-border-divider)',
      },
    }}
  />
));

export const ActionButtons = forwardRef<ActionButtonProps, 'button'>(
  ({ buttons, disableTooltips, ...groupProps }, ref) => {
    const resolvedButtons = buttons.filter(isNonNullable);

    // The shell paints its own border and surface, so an empty one shows as a dot.
    if (resolvedButtons.length === 0) {
      return null;
    }

    return (
      <ActionClusterShell {...groupProps} ref={ref}>
        {resolvedButtons.map((button, index) => {
          const { icon, tooltip, disabledTooltip, isDisabled, isLoading } = button;
          const glyph = icon as ComponentType<SVGProps<SVGSVGElement>>;
          const label = button['aria-label'] || tooltip || '';
          /* The documented swap: the same seat shows the action's tooltip or,
           * disabled, the reason. The built-in pairing stands down inside the
           * seat; tooltip={false} keeps a button whose config carries no
           * tooltip text as silent as it was before. */
          const content = isDisabled ? disabledTooltip : tooltip;

          return (
            <Tooltip
              key={tooltip ?? disabledTooltip ?? index}
              content={content ?? ''}
              isDisabled={disableTooltips || !content}
            >
              {'href' in button ? (
                <IconButton
                  variant="ghost"
                  icon={glyph}
                  label={label}
                  tooltip={false}
                  isDisabled={isDisabled}
                  href={button.href}
                  target={button.isExternal ? '_blank' : undefined}
                />
              ) : (
                <IconButton
                  variant="ghost"
                  icon={glyph}
                  label={label}
                  tooltip={false}
                  isDisabled={isDisabled}
                  isPending={isLoading}
                  /* Presses do not bubble, so the stopPropagation the old
                   * handler needed to protect the parent row click is now the
                   * default. */
                  onPress={() => button.onClick()}
                />
              )}
            </Tooltip>
          );
        })}
      </ActionClusterShell>
    );
  },
);
